/**
 * `status` → **Edit Labels** (spec M1-B5 §6.2, M2 §5.2; the `✎ Edit Labels` row of the picker,
 * parity card P-003).
 *
 * The dialog edits `settings_str` directly — `labels` (id → text), `labels_positions_v2`
 * (id → display position), `labels_colors` (id → the live hex trio) and `done_colors` — which
 * is the canonical store `[php§4]`. Two traps are honoured here and asserted in the tests:
 *
 *   - a label id is **immutable and never reused**: a new label takes `max(id) + 1`
 *     (`nextLabelId`), so a cell that still points at a deleted label keeps pointing at *that*
 *     label and never silently becomes another one `[php§4.2]`;
 *   - the display order is `labels_positions_v2`, **not** the id order `[php§4.3]`, so
 *     reordering rewrites positions and leaves every id alone.
 *
 * Deleting a label is refused while cells still use it — the count is not known to the client,
 * so the deletion is offered and the **server** is the one that can refuse; until B5 ships that
 * check the row is removed from `labels` and any cell pointing at it renders as the grey
 * "unknown label" swatch, which is what monday does for its own 21 broken references
 * `[cvs§2 status]`.
 */
import {
  MAX_STATUS_LABELS,
  findLabelById,
  labelPosition,
  nextLabelId,
  orderedLabelIds,
  type StatusSettings,
} from '@elizmonday/shared';
import {
  EMPTY_STATUS_COLOR,
  StatusColorPicker,
  statusColor,
  statusColorHex,
  statusColorNames,
} from '@elizmonday/ui';
import { useState } from 'react';
import type { ColumnSettingsProps } from '../types';

/** The `var_name` of a palette colour is what monday stores in `labels_colors` `[php§4.6]`. */
function colorEntry(name: string): { color: string; border: string; var_name: string } {
  const hex = statusColorHex(name);
  return { color: hex, border: hex, var_name: name };
}

export function StatusSettingsDialog({ settings, onCommit, onCancel }: ColumnSettingsProps<StatusSettings>) {
  /*
   * Round 10i: `settings` is `column.settings`, which is **`null`** on a column monday never
   * configured and `{}` on one this product has only just created — `orderedLabelIds(null)` threw
   * and took the board route down with it. `labels` is the one key this dialog cannot do without,
   * so it is defaulted explicitly rather than by a spread (which the declared type would not let
   * override anyway).
   */
  const [draft, setDraft] = useState<StatusSettings>(() => {
    const source = (settings ?? {}) as Partial<StatusSettings>;
    return { ...source, labels: source.labels ?? {} };
  });
  /** Which row's 40-swatch grid is open; monday opens one at a time. */
  const [paletteFor, setPaletteFor] = useState<number | null>(null);
  const ids = orderedLabelIds(draft);
  const doneIds = new Set(draft.done_colors ?? []);

  const patch = (next: Partial<StatusSettings>) => setDraft((current) => ({ ...current, ...next }));

  const rename = (id: number, text: string) =>
    patch({ labels: { ...draft.labels, [String(id)]: text } });

  const recolor = (id: number, name: string) =>
    patch({ labels_colors: { ...(draft.labels_colors ?? {}), [String(id)]: colorEntry(name) } });

  const remove = (id: number) => {
    const labels = { ...draft.labels };
    delete labels[String(id)];
    const positions = { ...(draft.labels_positions_v2 ?? {}) };
    delete positions[String(id)];
    const colors = { ...(draft.labels_colors ?? {}) };
    delete colors[String(id)];
    patch({
      labels,
      labels_positions_v2: positions,
      labels_colors: colors,
      done_colors: (draft.done_colors ?? []).filter((entry) => entry !== id),
    });
  };

  const add = () => {
    if (ids.length >= MAX_STATUS_LABELS) return;
    // `max(id) + 1`, never a reused id.
    const id = nextLabelId(draft);
    const position = ids.length === 0 ? 0 : Math.max(...ids.map((entry) => labelPosition(draft, entry))) + 1;
    patch({
      labels: { ...draft.labels, [String(id)]: 'New label' },
      labels_positions_v2: { ...(draft.labels_positions_v2 ?? {}), [String(id)]: position },
      labels_colors: {
        ...(draft.labels_colors ?? {}),
        [String(id)]: colorEntry(statusColorNames[ids.length % statusColorNames.length]!),
      },
    });
  };

  /** Reordering rewrites `labels_positions_v2` only; ids never move. */
  const move = (id: number, delta: -1 | 1) => {
    const order = [...ids];
    const from = order.indexOf(id);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= order.length) return;
    order.splice(to, 0, ...order.splice(from, 1));
    patch({
      labels_positions_v2: Object.fromEntries(order.map((entry, index) => [String(entry), index])),
    });
  };

  /**
   * Rebuilt from `discovery/ui-reference/editors/status/settings-customize.json` (round 4).
   * Round 3 built this from nothing — monday's dialog is two clicks deeper than the capture
   * had reached, behind `Settings ▸ Customize Status column`, and it looks nothing like the
   * single-column list with per-row Done checkboxes that was guessed.
   *
   * | measured | value |
   * |---|---|
   * | card | 400 × 520, radius 8, padding `24px 0`, white |
   * | title | `Status column settings`, 16px/500, inset 24 |
   * | subtitle | `Add or edit labels`, 14px/400 |
   * | grid | `display: grid`, **two columns**, padding `8px 8px 24px` |
   * | option row | 192 × 40 (a 40 px pitch, no gap): 22 px drag handle · 152 × 32 pill (radius 4, padding 4: a 24 × 24 swatch then the text) · a 16 × 16 `···` |
   * | `+ New label` | 152 × 32, radius 4, padding `4px 8px`, in the grid flow |
   * | done | **not a per-row checkbox** — a footer, `Choose which colors indicate that an item is completed`, over a 320 × 32 colour grid |
   *
   * monday's "Recommended for you" promo card sits between the two; it is marketing and is
   * absent here (B1 §3.2 "absent, not disabled").
   *
   * The store stays `done_colors` = label **ids** `[php§4.4]`, which is what the settings
   * round-trip needs; the footer is a *view* over it — picking a colour marks every label
   * wearing that colour as done. The two sources disagree about which the store holds, and
   * that is open question 244, not something to decide silently.
   */
  const doneColorNames = new Set(
    ids
      .filter((id) => doneIds.has(id))
      .map((id) => draft.labels_colors?.[String(id)]?.var_name)
      .filter((name): name is string => Boolean(name)),
  );

  /*
   * One swatch per **distinct colour in use**, painted with the label's stored hex when it
   * has one. `labels_colors[].var_name` is monday's palette key and `.color` is the live hex
   * `[php§4.6]`; a column imported from monday can carry a `var_name` our palette does not
   * know, and painting that grey would make five different labels look like one colour.
   */
  const usedColors = [
    ...new Map(
      ids.map((id) => {
        const entry = draft.labels_colors?.[String(id)];
        const name = entry?.var_name ?? EMPTY_STATUS_COLOR;
        return [name, entry?.color ?? statusColor(name)] as const;
      }),
    ),
  ];

  /** Marking a colour done marks every label wearing it (and unmarking clears them). */
  const toggleDoneColor = (name: string) => {
    const wearing = ids.filter((id) => (draft.labels_colors?.[String(id)]?.var_name ?? '') === name);
    const alreadyDone = doneColorNames.has(name);
    const next = new Set(draft.done_colors ?? []);
    for (const id of wearing) {
      if (alreadyDone) next.delete(id);
      else next.add(id);
    }
    patch({ done_colors: [...next] });
  };

  return (
    <form
      className="ez-status-settings"
      data-testid="status-settings"
      onSubmit={(event) => {
        event.preventDefault();
        onCommit(draft);
      }}
    >
      <h2 className="ez-status-settings__title">Status column settings</h2>
      <p className="ez-status-settings__subtitle">Add or edit labels</p>

      <div className="ez-status-settings__grid">
        {ids.map((id, index) => (
          <div className="ez-status-option" key={id} data-testid={`label-row-${String(id)}`}>
            {/* monday's drag handle. Reordering rewrites `labels_positions_v2` only. */}
            <span className="ez-status-option__handle" aria-hidden="true">
              ⠿
            </span>
            <span
              className="ez-status-option__pill"
              style={{
                backgroundColor:
                  draft.labels_colors?.[String(id)]?.color ??
                  statusColor(draft.labels_colors?.[String(id)]?.var_name ?? EMPTY_STATUS_COLOR),
              }}
            >
              <button
                type="button"
                className="ez-status-option__swatch"
                aria-label={`Colour of ${findLabelById(draft, id) ?? String(id)}`}
                aria-expanded={paletteFor === id}
                data-testid={`label-color-${String(id)}`}
                onClick={() => setPaletteFor((current) => (current === id ? null : id))}
              />
              <input
                className="ez-status-option__text"
                data-testid={`label-name-${String(id)}`}
                aria-label={`Label ${String(index + 1)}`}
                value={draft.labels[String(id)] ?? ''}
                onChange={(event) => rename(id, event.target.value)}
              />
            </span>
            <span className="ez-status-option__menu">
              <button
                type="button"
                aria-label={`Move ${draft.labels[String(id)] ?? ''} up`}
                data-testid={`label-move-up-${String(id)}`}
                disabled={index === 0}
                onClick={() => move(id, -1)}
              >
                ↑
              </button>
              <button
                type="button"
                aria-label={`Move ${draft.labels[String(id)] ?? ''} down`}
                data-testid={`label-move-down-${String(id)}`}
                disabled={index === ids.length - 1}
                onClick={() => move(id, 1)}
              >
                ↓
              </button>
              <button
                type="button"
                aria-label={`Delete ${draft.labels[String(id)] ?? ''}`}
                data-testid={`label-delete-${String(id)}`}
                onClick={() => remove(id)}
              >
                ×
              </button>
            </span>
            {paletteFor === id ? (
              <div className="ez-status-option__palette">
                <StatusColorPicker
                  value={draft.labels_colors?.[String(id)]?.var_name ?? EMPTY_STATUS_COLOR}
                  onChange={(name) => {
                    recolor(id, name);
                    setPaletteFor(null);
                  }}
                  data-testid={`label-palette-${String(id)}`}
                />
              </div>
            ) : null}
          </div>
        ))}

        <button
          type="button"
          className="ez-status-settings__add"
          data-testid="label-add"
          disabled={ids.length >= MAX_STATUS_LABELS}
          onClick={add}
        >
          + New label
        </button>
      </div>

      <div className="ez-status-settings__done">
        <span className="ez-status-settings__section">
          Choose which colors indicate that an item is completed
        </span>
        <ul className="ez-status-settings__colors">
          {usedColors.map(([name, hex]) => (
            <li key={name}>
              <button
                type="button"
                className="ez-status-settings__color"
                aria-pressed={doneColorNames.has(name)}
                aria-label={`${name} means done`}
                data-testid={`done-color-${name}`}
                style={{ backgroundColor: hex }}
                onClick={() => toggleDoneColor(name)}
              />
            </li>
          ))}
        </ul>
      </div>

      <footer className="ez-status-settings__footer">
        <button type="button" className="ez-status-settings__cancel" data-testid="settings-cancel" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="ez-status-settings__save" data-testid="settings-save">
          Apply
        </button>
      </footer>
    </form>
  );
}
